import { IconArrowLeft } from '@/components/icons/IconArrowLeft';
import { IconArrowRight } from '@/components/icons/IconArrowRight';
import type { NavItemDto } from '@/lib/sanity/dto';
import { cn } from '@/lib/utils/tw-merge';
import { BaseLink } from './BaseLink';

export type CtaVariant = 'solid' | 'invert' | 'ghost';

type CtaButtonProps = {
    item?: NavItemDto | null;
    /**
     * `solid` spends the accent, `invert` fills with the page's own ink, `ghost`
     * is a hairline outline. A section picks the pair its design calls for.
     */
    variant?: CtaVariant;
    size?: 'md' | 'lg';
    /** `back` puts the arrow first and points it the other way. */
    direction?: 'forward' | 'back';
    className?: string;
};

const VARIANT_CLASS: Record<CtaVariant, string> = {
    solid: 'btn-primary',
    invert: 'btn-light',
    ghost: 'btn-white-outline',
};

/**
 * A call to action that goes somewhere. The arrow is part of it, not decoration:
 * it is what separates "this takes you elsewhere" from "this does something
 * here", and every button in the design that leads away carries one.
 */
export function CtaButton({ item, variant = 'solid', size = 'md', direction = 'forward', className }: CtaButtonProps) {
    if (!item?.label) return null;

    const isBack = direction === 'back';

    return (
        <BaseLink item={item} className={cn('btn', VARIANT_CLASS[variant], size === 'lg' && 'btn-lg', className)}>
            {isBack ? <IconArrowLeft /> : null}
            {item.label}
            {isBack ? null : <IconArrowRight />}
        </BaseLink>
    );
}
